/**
 * AIAvatarPage (Reference Persons Page)
 *
 * Allows users to manage reference persons (AI Avatars) for character-consistent
 * AI image generation. Each person can have up to 5 reference images.
 */

import { useState, useCallback } from 'react';
import { PostWriterSubmenuMemo } from '@/components/post-writer/PostWriterSubmenu';
import { ReferencePersonList } from '@/components/ai-avatar/ReferencePersonList';
import { ReferencePersonForm } from '@/components/ai-avatar/ReferencePersonForm';
import { ReferenceImageUpload } from '@/components/ai-avatar/ReferenceImageUpload';
import { DeletePersonDialog } from '@/components/ai-avatar/DeletePersonDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useReferencePersons } from '@/hooks/useReferencePersons';
import { Plus } from 'lucide-react';
import type { Id } from '../../convex/_generated/dataModel';
import type { IReferencePersonFormInput } from '@/@types/referencePersons';
import { REFERENCE_PERSON_CONSTANTS } from '@/@types/referencePersons';
import { cn } from '@/lib/utils';

export const AIAvatarPage = () => {
  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingPersonId, setEditingPersonId] = useState<Id<'referencePersons'> | null>(null);
  const [uploadingPersonId, setUploadingPersonId] = useState<Id<'referencePersons'> | null>(null);
  const [deletingPersonId, setDeletingPersonId] = useState<Id<'referencePersons'> | null>(null);

  // Hook for CRUD operations
  const {
    persons,
    personCount,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    createPerson,
    updatePerson,
    deletePerson,
    setDefault,
    canCreatePerson,
  } = useReferencePersons();

  // Create person handler
  const handleCreateSubmit = useCallback(
    async (values: IReferencePersonFormInput) => {
      const result = await createPerson(values);
      if (result.success) {
        setIsCreateDialogOpen(false);
      }
    },
    [createPerson]
  );

  // Edit person handler
  const handleEditSubmit = useCallback(
    async (values: IReferencePersonFormInput) => {
      if (!editingPersonId) return;
      const result = await updatePerson(editingPersonId, values);
      if (result.success) {
        setEditingPersonId(null);
      }
    },
    [editingPersonId, updatePerson]
  );

  // Delete person handler
  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingPersonId) return;
    const result = await deletePerson(deletingPersonId);
    if (result.success) {
      setDeletingPersonId(null);
    }
  }, [deletingPersonId, deletePerson]);

  // Set default person handler
  const handleSetDefault = useCallback(
    async (personId: Id<'referencePersons'>) => {
      await setDefault(personId);
    },
    [setDefault]
  );

  // Image handlers
  const handleImageAdded = useCallback(() => {
    // Image added via hook, UI will update via Convex reactivity
  }, []);

  const handleImageRemoved = useCallback(() => {
    // Image removed via hook, UI will update via Convex reactivity
  }, []);

  // Find persons for dialogs
  const editingPerson = persons.find((p) => p._id === editingPersonId);
  const uploadingPerson = persons.find((p) => p._id === uploadingPersonId);
  const deletingPerson = persons.find((p) => p._id === deletingPersonId);

  return {
    submenu: <PostWriterSubmenuMemo />,
    mainContent: (
      <div className="h-full bg-background">
        <div className="p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex flex-col gap-2">
              <h1 className="text-[28px] font-bold text-foreground">
                Reference Persons
              </h1>
              <p className="text-sm text-muted-foreground">
                Create reference persons for character-consistent image generation.
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Person Count Badge */}
              <div className="px-3 py-1.5 rounded-md bg-muted">
                <span className="text-[13px] font-medium text-muted-foreground">
                  {personCount}/{REFERENCE_PERSON_CONSTANTS.MAX_PERSONS_PER_USER} persons
                </span>
              </div>

              {/* New Person Button */}
              <button
                type="button"
                onClick={() => setIsCreateDialogOpen(true)}
                disabled={!canCreatePerson}
                className={cn(
                  'flex items-center gap-2 px-5 py-2.5 rounded-lg',
                  'bg-primary text-primary-foreground hover:bg-primary/90',
                  'transition-colors duration-150',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'font-semibold text-sm'
                )}
              >
                <Plus className="w-[18px] h-[18px]" />
                New Person
              </button>
            </div>
          </div>

          {/* Content */}
          <ReferencePersonList
            persons={persons}
            isLoading={isLoading}
            onEdit={(personId) => setEditingPersonId(personId)}
            onDelete={(personId) => setDeletingPersonId(personId)}
            onViewImages={(personId) => setUploadingPersonId(personId)}
            onSetDefault={handleSetDefault}
            onAddPerson={() => setIsCreateDialogOpen(true)}
            canAddPerson={canCreatePerson}
          />

          {/* Create Dialog */}
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogContent showCloseButton={false} className="sm:max-w-[480px] p-6">
              <ReferencePersonForm
                mode="create"
                onSubmit={handleCreateSubmit}
                onCancel={() => setIsCreateDialogOpen(false)}
                isSubmitting={isCreating}
              />
            </DialogContent>
          </Dialog>

          {/* Edit Dialog */}
          <Dialog
            open={editingPersonId !== null}
            onOpenChange={(open) => !open && setEditingPersonId(null)}
          >
            <DialogContent showCloseButton={false} className="sm:max-w-[480px] p-6">
              {editingPerson && (
                <ReferencePersonForm
                  mode="edit"
                  initialValues={{
                    name: editingPerson.name,
                    description: editingPerson.description,
                  }}
                  onSubmit={handleEditSubmit}
                  onCancel={() => setEditingPersonId(null)}
                  isSubmitting={isUpdating}
                />
              )}
            </DialogContent>
          </Dialog>

          {/* Upload Images Dialog */}
          <Dialog
            open={uploadingPersonId !== null}
            onOpenChange={(open) => !open && setUploadingPersonId(null)}
          >
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Manage Images - {uploadingPerson?.name}</DialogTitle>
              </DialogHeader>
              {uploadingPerson && (
                <ReferenceImageUpload
                  personId={uploadingPerson._id}
                  currentImages={uploadingPerson.imageIds.map((id, i) => ({
                    id,
                    url: uploadingPerson.imageUrls[i] || '',
                  }))}
                  maxImages={REFERENCE_PERSON_CONSTANTS.MAX_IMAGES_PER_PERSON}
                  onImageAdded={handleImageAdded}
                  onImageRemoved={handleImageRemoved}
                />
              )}
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <DeletePersonDialog
            personName={deletingPerson?.name || ''}
            isOpen={deletingPersonId !== null}
            onClose={() => setDeletingPersonId(null)}
            onConfirm={handleDeleteConfirm}
            isDeleting={isDeleting}
          />
        </div>
      </div>
    ),
  };
};
